import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { humanise, isRelevant } from './schemaForm';
import type { JsonSchema, JsonValue, UiField } from './schemaForm';

/**
 * One entry of Control's `GET /v1/pipelines/manifest`, as the portal reads it.
 *
 * Mirrors `PipelineDescriptor` in Control's `lib/pipelines/manifest.ts` — the
 * SHAPE of the document, not its contents. Nothing here names a pipeline or a
 * field; every member the portal does not understand is ignored, which is how
 * Control can add to a descriptor without a change here.
 */
export interface PipelineDescriptor {
  id: string;
  title: string;
  summary: string;
  clouds?: string[];
  /** The route Control takes the request on. Read server-side only. */
  endpoint?: string;
  status?: 'ga' | 'beta' | 'devops-only' | string;
  approval?: { policy?: string; alwaysDevOpsWhen?: string[] };
  schema: JsonSchema;
  ui: {
    groups: {
      title: string;
      hint?: string;
      fields: string[];
      /** Advanced WITHIN this group — bucket lifetime beside the buckets. */
      advanced?: string[];
    }[];
    advanced: string[];
    /** Per-field presentation. Sparse; absent means render from the schema. */
    fields?: Record<string, UiField>;
  };
  docs?: string;
}

/**
 * Read the manifest through the portal's backend.
 *
 * No caller token: the manifest is a capability document — identical for
 * everyone — and the backend reads it with the PORTAL's own identity (see
 * `ControlClient.pipelineManifest`). Only the request that acts carries the
 * caller's.
 */
export async function fetchPipelineManifest(
  discovery: DiscoveryApi,
  fetchApi: FetchApi,
): Promise<PipelineDescriptor[]> {
  const baseUrl = await discovery.getBaseUrl('control');
  const response = await fetchApi.fetch(`${baseUrl}/pipelines/manifest`);
  if (!response.ok) {
    throw new Error(`${response.status} ${await response.text()}`);
  }
  const body = (await response.json()) as { pipelines?: PipelineDescriptor[] };
  return body.pipelines ?? [];
}

/**
 * Answers as the form should SEE them: what was typed, over the schema's
 * defaults.
 *
 * `showWhen` is evaluated against these, not against what was typed. `cloud`
 * defaults to `gcp`, and nobody clicks the radio that is already selected —
 * so evaluated against typed answers alone, every `showWhen: cloud = gcp` field
 * would stay hidden on a form that visibly says GCP.
 *
 * Defaults are NOT what gets sent. Control's Zod applies its own; sending them
 * back would turn "whatever the default is" into a pinned value.
 */
export function effectiveValues(
  schema: JsonSchema | undefined,
  values: Record<string, JsonValue>,
): Record<string, JsonValue> {
  const defaults: Record<string, JsonValue> = {};
  for (const [name, property] of Object.entries(schema?.properties ?? {})) {
    if (property.default !== undefined) {
      defaults[name] = property.default as JsonValue;
    }
  }
  return { ...defaults, ...values };
}

/**
 * Which fields are in play, given the answers so far.
 *
 * Transitive: `expiresOn` shows when `lifecycle` is `engagement`, and
 * `lifecycle` shows only for AWS. Switch back to GCP and `lifecycle` is hidden
 * but still holds `engagement` — a field depending on a hidden field is
 * hidden too, or a GCP form would keep asking when the client engagement ends.
 */
export function relevantFields(
  pipeline: PipelineDescriptor,
  values: Record<string, JsonValue>,
): Set<string> {
  const names = Object.keys(pipeline.schema.properties ?? {});
  const effective = effectiveValues(pipeline.schema, values);
  const ui = pipeline.ui.fields ?? {};
  /** One pass: what survives, given what survived the last pass. */
  const pass = (current: Set<string>) =>
    new Set(
      names.filter(name => {
        const when = ui[name]?.showWhen;
        if (!when) return true;
        // A condition on a field that is itself out of play cannot hold. A
        // condition on a field the schema does not have resolves toward
        // visible, as isRelevant does — an input hidden forever is worse.
        if (
          names.includes(when.field) &&
          !current.has(when.field) &&
          when.field !== name
        ) {
          return false;
        }
        return isRelevant(ui[name], effective);
      }),
    );
  // Each pass can only remove; the chain is at most as long as the field list.
  let relevant = new Set(names);
  for (let i = 0; i < names.length; i++) {
    const next = pass(relevant);
    if (next.size === relevant.size) return next;
    relevant = next;
  }
  return relevant;
}

/**
 * What is actually sent: the answers to the questions that are still being
 * asked.
 *
 * Somebody who starts on AWS, types a workload, then picks GCP has a `workload`
 * sitting in state. Sending it would ask Control to judge a GCP request with an
 * AWS field in it — at best stripped, at worst refused by a strict schema —
 * and either way it is not what the form showed them when they pressed Submit.
 */
export function requestBody(
  pipeline: PipelineDescriptor,
  values: Record<string, JsonValue>,
): Record<string, JsonValue> {
  const relevant = relevantFields(pipeline, values);
  return Object.fromEntries(
    Object.entries(values).filter(
      ([name, value]) =>
        relevant.has(name) &&
        value !== undefined &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0),
    ),
  );
}

/** One problem with one field, in the shape Control's 400 uses. */
export interface FieldIssue {
  /** Top-level field name, or undefined for an issue about the whole request. */
  field?: string;
  message: string;
}

const present = (value: JsonValue | undefined) =>
  value !== undefined &&
  value !== null &&
  value !== '' &&
  !(Array.isArray(value) && value.length === 0);

/**
 * The checks the schema states plainly, run before the request leaves.
 *
 * Deliberately not a JSON Schema validator. Control is the authority — it
 * validates against its own Zod, including the cross-field rules a schema
 * cannot say (a GCP request needs a project id; an engagement needs an end
 * date) — and its 400 is rendered field by field. This catches the cheap
 * mistakes without a round trip: a required answer left empty, a value the
 * schema's own pattern or bounds refuse.
 */
export function validateRequest(
  pipeline: PipelineDescriptor,
  values: Record<string, JsonValue>,
): FieldIssue[] {
  const issues: FieldIssue[] = [];
  const relevant = relevantFields(pipeline, values);
  const properties = pipeline.schema.properties ?? {};
  const required = new Set(pipeline.schema.required ?? []);

  for (const [name, schema] of Object.entries(properties)) {
    if (!relevant.has(name)) continue;
    const value = values[name];
    if (!present(value)) {
      if (required.has(name) && schema.default === undefined) {
        issues.push({ field: name, message: 'Required.' });
      }
      continue;
    }
    if (typeof value === 'string') {
      if (schema.pattern && !new RegExp(schema.pattern).test(value)) {
        issues.push({
          field: name,
          message: `"${value}" is not in the expected format.`,
        });
      } else if (
        schema.minLength !== undefined &&
        value.length < schema.minLength
      ) {
        issues.push({
          field: name,
          message: `At least ${schema.minLength} characters.`,
        });
      } else if (
        schema.maxLength !== undefined &&
        value.length > schema.maxLength
      ) {
        issues.push({
          field: name,
          message: `At most ${schema.maxLength} characters.`,
        });
      }
    }
    if (typeof value === 'number') {
      if (Number.isNaN(value)) {
        issues.push({ field: name, message: 'Must be a number.' });
      } else if (schema.minimum !== undefined && value < schema.minimum) {
        issues.push({ field: name, message: `At least ${schema.minimum}.` });
      } else if (schema.maximum !== undefined && value > schema.maximum) {
        issues.push({ field: name, message: `At most ${schema.maximum}.` });
      }
    }
    if (Array.isArray(value) && schema.items?.pattern) {
      const pattern = new RegExp(schema.items.pattern);
      const bad = value.filter(v => typeof v === 'string' && !pattern.test(v));
      if (bad.length) {
        issues.push({
          field: name,
          message: `Not in the expected format: ${bad.join(', ')}.`,
        });
      }
    }
  }
  return issues;
}

/**
 * Control's `400 { error, issues[] }`, as field issues.
 *
 * Zod issues carry a `path`; the AWS pipeline's cross-field checks carry only
 * a `message`. Both are kept — a message with no field is shown at the top of
 * the form rather than dropped, because "an engagement needs expiresOn" is
 * exactly the kind of thing only Control can know.
 */
export function issuesFromRejection(body: unknown): FieldIssue[] {
  const rejection = (body ?? {}) as {
    error?: string;
    issues?: { path?: (string | number)[]; message?: string }[];
  };
  const issues = (rejection.issues ?? []).map(issue => ({
    field:
      typeof issue.path?.[0] === 'string' ? String(issue.path[0]) : undefined,
    message: issue.message ?? 'Invalid.',
  }));
  if (!issues.length) {
    issues.push({
      field: undefined,
      message: rejection.error ?? 'Control refused the request.',
    });
  }
  return issues;
}

/** "Workload: Required." — for the summary above the form. */
export function describeIssue(
  pipeline: PipelineDescriptor | undefined,
  issue: FieldIssue,
): string {
  if (!issue.field) return issue.message;
  const label =
    pipeline?.ui.fields?.[issue.field]?.label ??
    pipeline?.schema.properties?.[issue.field]?.title ??
    humanise(issue.field);
  return `${label}: ${issue.message}`;
}
